
import React, { useState, useEffect } from 'react';
import { PopupContent } from '@/types/popup-system';

interface AnalogHorrorPopupProps {
  content: PopupContent;
  onDismiss: () => void;
  onAction?: (action: string) => void;
}

export const AnalogHorrorPopup = ({ content, onDismiss, onAction }: AnalogHorrorPopupProps) => {
  const [glitching, setGlitching] = useState(false);
  const [staticNoise, setStaticNoise] = useState(false);

  useEffect(() => {
    const glitchTimer = setInterval(() => {
      if (Math.random() < 0.3) {
        setGlitching(true);
        setTimeout(() => setGlitching(false), 150);
      }
    }, 2000);

    const staticTimer = setInterval(() => {
      if (Math.random() < 0.2) {
        setStaticNoise(true);
        setTimeout(() => setStaticNoise(false), 100);
      }
    }, 1000);

    return () => {
      clearInterval(glitchTimer);
      clearInterval(staticTimer);
    };
  }, []);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90">
      <div 
        className={`bg-black border-2 border-red-600 max-w-md w-full mx-4 relative overflow-hidden ${
          glitching ? 'animate-pulse' : ''
        }`}
        style={{
          boxShadow: '0 0 20px rgba(255, 0, 0, 0.5)',
          filter: glitching ? 'hue-rotate(90deg) contrast(1.5)' : 'none'
        }}
      >
        {/* Static noise overlay */}
        {staticNoise && (
          <div 
            className="absolute inset-0 opacity-30 pointer-events-none"
            style={{
              backgroundImage: `url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADIAAAAyCAYAAAAeP4ixAAAABHNCSVQICAgIfAhkiAAAAAlwSFlzAAAAdgAAAHYBTnsmCAAAABl0RVh0U29mdHdhcmUAd3d3Lmlua3NjYXBlLm9yZ5vuPBoAAANOSURBVGiB7ZrPbxJBFMafWWBAXFpb")`,
              backgroundSize: '100px 100px'
            }}
          />
        )}
        
        {/* Scan lines */}
        <div 
          className="absolute inset-0 pointer-events-none opacity-20"
          style={{
            backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,0,0,0.1) 2px, rgba(255,0,0,0.1) 4px)',
          }}
        />

        <div className="relative z-10">
          <div className="bg-red-900 text-red-100 px-3 py-2 flex justify-between items-center font-mono">
            <span className="text-sm font-bold glitch-text">{content.title}</span>
            {content.dismissible && (
              <button 
                onClick={onDismiss}
                className="hover:bg-red-800 px-2 py-0.5 text-xs"
              >
                ✕
              </button>
            )}
          </div>
          
          <div className="p-4 font-mono text-red-300 bg-black">
            <div className="border border-red-600 p-3 bg-red-950/20">
              <p className="leading-relaxed whitespace-pre-wrap">
                {content.message}
              </p>
            </div>
            
            {content.actionButtons && content.actionButtons.length > 0 && (
              <div className="mt-4 flex gap-2">
                {content.actionButtons.map((button, index) => (
                  <button
                    key={index}
                    onClick={() => onAction?.(button.action)}
                    className="border border-red-600 bg-red-950 text-red-300 px-4 py-2 hover:bg-red-800 hover:text-red-100 transition-colors font-mono text-sm"
                  >
                    {button.text}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
